import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AccountStateService } from './account-state.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService {
  jwtHelper = new JwtHelperService();

  constructor(
    private authService: AuthService,
    private router: Router,
    private accountStateService: AccountStateService,
  ) {}

  /**
   * Whether a route requiring auth can be activated
   */
  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    const accessToken = localStorage.getItem(
      this.authService.accessTokenLocalStorageKey,
    );

    // If we have a non-expired access token, we can activate
    if (accessToken && !this.jwtHelper.isTokenExpired(accessToken)) {
      return true;
    }

    if (accessToken && this.jwtHelper.isTokenExpired(accessToken)) {
      return this.tryRefreshSession(state);
    }

    // Either access token is not set, or we could not refresh token
    // We should return a UrlTree for the login page, and set the returnUrl so the user can proceed to whatever
    // they were trying to view
    return this.createLoginRedirect(state);
  }

  private async tryRefreshSession(
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    try {
      if (!localStorage.getItem(this.authService.refreshTokenLocalStorageKey)) {
        return this.clearAuthAndRedirect(state);
      }

      const refreshResponse = await lastValueFrom(
        this.authService.refreshToken(),
      );

      if (refreshResponse?.status === 200) {
        this.authService.setTokens(refreshResponse.body);
        const accountSelection =
          await this.accountStateService.refreshAccountsAndReconcileSelected(
            refreshResponse.body.selectedAccountGuid,
          );

        if (accountSelection.status === 'selected') {
          return true;
        }
      }
    } catch {
      return this.clearAuthAndRedirect(state);
    }

    return this.clearAuthAndRedirect(state);
  }

  private clearAuthAndRedirect(state: RouterStateSnapshot): UrlTree {
    this.authService.clearAuthState();
    return this.createLoginRedirect(state);
  }

  private createLoginRedirect(state: RouterStateSnapshot): UrlTree {
    const redirectUrl = this.router.parseUrl('/');
    redirectUrl.queryParams['returnUrl'] = state.url;
    return redirectUrl;
  }
}
